import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Loader2, RefreshCw, Check, X, MapPin, ShieldAlert, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { trashScanBefore, trashScanAfter, downscaleImage, getGps, humanErr } from "@/lib/trashApi";

/** Two-step flow — Before → Cleanup instruction → After. Camera only, hard-blocked. */
export default function TrashScan() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("intro");   // intro | capturing_before | detected | picking_up | capturing_after | done
  const [detected, setDetected] = useState(null);  // before-scan result
  const [awarded, setAwarded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState("");
  const beforeInputRef = useRef(null);
  const afterInputRef = useRef(null);
  const [isMobile, setIsMobile] = useState(true);

  // Detect mobile via user-agent — desktop shows "Use your phone" message.
  useEffect(() => {
    const ua = navigator.userAgent || "";
    setIsMobile(/Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(ua));
  }, []);

  useEffect(() => {
    if (!user) return;
    getGps().then((g) => { if (g) setGps(g); else setGpsError("Enable location for verified rewards."); });
  }, [user]);

  if (loading) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!user) return (
    <div className="py-16 text-center" data-testid="scan-signin-required">
      <div className="font-black text-2xl mb-2">Sign in to scan</div>
      <Link to="/login?next=/trashhunter/scan" className="inline-flex items-center gap-1 bg-[#39FF14] text-black font-black text-xs uppercase tracking-widest px-5 py-2.5 rounded-full mt-3">Sign in</Link>
    </div>
  );

  if (!isMobile) return (
    <div className="rounded-3xl border-2 border-[#FF00E5]/40 bg-[#FF00E5]/10 p-6 text-center" data-testid="scan-mobile-only">
      <ShieldAlert className="w-10 h-10 mx-auto text-[#FF00E5] mb-3" />
      <div className="font-black text-2xl">Use your phone</div>
      <div className="text-white/70 text-sm mt-2">Jixxeo Trash Hunter needs a live camera + GPS. Open trash hunter on your phone to start scanning real litter.</div>
    </div>
  );

  const captureBefore = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const img = await downscaleImage(file, { maxSize: 1280, quality: 0.85 });
      let g = gps;
      if (!g) g = await getGps();
      const body = {
        image_base64: img.base64, mime: "image/jpeg",
        gps_lat: g?.lat ?? null, gps_lon: g?.lon ?? null, gps_acc_m: g?.acc ?? null,
      };
      const r = await trashScanBefore(body);
      setDetected(r);
      setStep("detected");
      if (!g) setGpsError("No GPS — pickup rewards unverified. Enable location.");
    } catch (e) {
      toast.error(humanErr(e, "Scan failed. Please retry."));
      setStep("intro");
    } finally { setBusy(false); }
  };

  const captureAfter = async (file) => {
    if (!file || !detected?.scan_id) return;
    setBusy(true);
    try {
      const img = await downscaleImage(file, { maxSize: 1280, quality: 0.85 });
      let g = gps;
      if (!g) g = await getGps();
      const r = await trashScanAfter({
        before_scan_id: detected.scan_id,
        image_base64: img.base64, mime: "image/jpeg",
        gps_lat: g?.lat ?? null, gps_lon: g?.lon ?? null, gps_acc_m: g?.acc ?? null,
      });
      setAwarded(r);
      setStep("done");
      if (r.cleanup_id) {
        toast.success(`+${r.awarded.xp} XP · +${r.awarded.coins} coins`);
      } else {
        toast.error(r.reason || "Cleanup not verified — please retry.");
      }
    } catch (e) {
      toast.error(humanErr(e, "After-scan failed"));
    } finally { setBusy(false); }
  };

  const reset = () => { setStep("intro"); setDetected(null); setAwarded(null); };

  return (
    <div className="space-y-4" data-testid="trash-scan">
      <GpsBar gps={gps} error={gpsError} />

      {step === "intro" && (
        <IntroCard onOpen={() => { setStep("capturing_before"); beforeInputRef.current?.click(); }} busy={busy} />
      )}

      {step === "capturing_before" && busy && (
        <Spinner label="Jixxeo A.I is looking…" />
      )}

      {step === "detected" && detected && (
        <DetectedCard detected={detected} onCleaned={() => { setStep("capturing_after"); afterInputRef.current?.click(); }} onRetake={reset} />
      )}

      {step === "capturing_after" && busy && (
        <Spinner label="Verifying the cleanup…" />
      )}

      {step === "done" && awarded && (
        <DoneCard awarded={awarded} onAgain={reset} onWallet={() => nav("/trashhunter/wallet")} />
      )}

      {/* Hard-blocked camera inputs. `capture="environment"` forces live camera on iOS/Android;
          on desktop this attribute is ignored but the isMobile block above blocks desktop entirely. */}
      <input ref={beforeInputRef} type="file" accept="image/*" capture="environment"
        onChange={(e) => captureBefore(e.target.files?.[0])} className="hidden"
        data-testid="scan-before-input" />
      <input ref={afterInputRef} type="file" accept="image/*" capture="environment"
        onChange={(e) => captureAfter(e.target.files?.[0])} className="hidden"
        data-testid="scan-after-input" />
    </div>
  );
}

function GpsBar({ gps, error }) {
  return (
    <div data-testid="scan-gps"
      className={"rounded-2xl border px-3 py-2 text-xs flex items-center gap-2 " +
        (gps ? "border-[#39FF14]/30 bg-[#39FF14]/10 text-[#39FF14]" : "border-[#FFD700]/30 bg-[#FFD700]/10 text-[#FFD700]")}>
      <MapPin className="w-3 h-3" />
      {gps ? <span>GPS locked · accuracy ~{Math.round(gps.acc || 0)}m</span>
           : <span>{error || "Locating…"}</span>}
    </div>
  );
}

function IntroCard({ onOpen, busy }) {
  return (
    <div className="rounded-3xl border-2 border-[#39FF14]/40 bg-black/40 p-6 text-center space-y-3" data-testid="scan-intro">
      <Camera className="w-14 h-14 text-[#39FF14] mx-auto" />
      <div className="font-black text-2xl">Point at the litter</div>
      <div className="text-white/60 text-sm">Camera-only. Jixxeo A.I identifies the trash type and confidence.</div>
      <button data-testid="scan-open-camera" onClick={onOpen} disabled={busy}
        className="mt-2 inline-flex items-center gap-2 bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        Open camera
      </button>
      <div className="text-[10px] mono uppercase tracking-widest text-white/40 pt-2">
        Photos taken here are never uploaded to your gallery.
      </div>
    </div>
  );
}

function DetectedCard({ detected, onCleaned, onRetake }) {
  return (
    <div className="rounded-3xl border-2 border-[#39FF14]/40 bg-black/40 p-5 space-y-3" data-testid="scan-detected">
      <div className="flex items-center gap-3">
        <Sparkles className="w-8 h-8 text-[#39FF14]" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] mono uppercase tracking-widest text-[#39FF14]">Detected</div>
          <div className="font-black text-2xl capitalize">{(detected.detected_type || "trash").replace(/_/g, " ")}</div>
          <div className="text-xs text-white/60 mt-0.5">Confidence: {Math.round((detected.confidence || 0) * 100)}%
            {detected.hazard && detected.hazard !== "low" && <span className={"ml-2 px-2 py-0.5 rounded uppercase text-[9px] " + (detected.hazard === "high" ? "bg-[#F87171]/20 text-[#F87171]" : "bg-[#FFD700]/20 text-[#FFD700]")}>{detected.hazard} hazard</span>}
          </div>
        </div>
      </div>
      {detected.reason && <div className="text-xs text-white/50">{detected.reason}</div>}
      <div className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-3 text-sm">
        <div className="font-black text-[#FFD700] uppercase tracking-widest text-[10px] mb-1">Now pick it up</div>
        <div className="text-white/80">Bag the litter safely. When it's cleared, take the after photo from the same spot.</div>
      </div>
      <div className="flex gap-2">
        <button data-testid="scan-after-btn" onClick={onCleaned}
          className="flex-1 inline-flex items-center gap-2 justify-center bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-4 py-3 rounded-full">
          <Check className="w-4 h-4" /> Photo the after
        </button>
        <button data-testid="scan-retake" onClick={onRetake}
          className="inline-flex items-center gap-1 border border-white/20 bg-white/5 font-black text-xs uppercase tracking-widest px-4 py-3 rounded-full">
          <RefreshCw className="w-3 h-3" /> Retake
        </button>
      </div>
    </div>
  );
}

function DoneCard({ awarded, onAgain, onWallet }) {
  const good = !!awarded.cleanup_id;
  return (
    <div className={"rounded-3xl border-2 p-6 text-center space-y-3 " + (good ? "border-[#39FF14]/50 bg-[#39FF14]/10" : "border-[#F87171]/30 bg-[#F87171]/10")}
         data-testid="scan-done">
      {good ? <Check className="w-14 h-14 text-[#39FF14] mx-auto" /> : <X className="w-14 h-14 text-[#F87171] mx-auto" />}
      <div className="font-black text-3xl">{good ? "Nice work!" : "Try again"}</div>
      <div className="text-white/70 text-sm">{awarded.reason}</div>
      {good && (
        <div className="grid grid-cols-3 gap-2 pt-2">
          <Award label="XP" value={`+${awarded.awarded.xp}`} accent="#00F0FF" />
          <Award label="Coins" value={`+${awarded.awarded.coins}`} accent="#39FF14" />
          <Award label="Cash" value={awarded.awarded.cash_cents > 0 ? "+ Earned" : "—"} accent="#FFD700" />
        </div>
      )}
      {good && awarded.streak_day > 1 && <div className="text-[#FFD700] font-black text-sm">🔥 {awarded.streak_day}-day streak!</div>}
      <div className="flex gap-2 justify-center pt-3">
        <button data-testid="done-again" onClick={onAgain}
          className="bg-[#39FF14] text-black font-black text-xs uppercase tracking-widest px-6 py-3 rounded-full">Scan again</button>
        <button data-testid="done-wallet" onClick={onWallet}
          className="border border-white/30 bg-white/5 font-black text-xs uppercase tracking-widest px-6 py-3 rounded-full">Wallet</button>
      </div>
    </div>
  );
}

function Award({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 p-3">
      <div className="text-[9px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-black text-xl" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Spinner({ label }) {
  return (
    <div className="py-16 text-center" data-testid="scan-spinner">
      <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#39FF14]" />
      <div className="text-white/60 text-sm mt-3">{label}</div>
    </div>
  );
}
